import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @param throttleMs - Throttle delay in milliseconds (default: 100)
 * @returns Current client width in pixels
 */
function useClientWidth(throttleMs: number = 100): number {
  const getClientWidth = useCallback((): number => {
    if (typeof window === 'undefined' || !window.document) {
      return 0;
    }

    try {
      return (
        window.document.documentElement?.clientWidth ?? window.innerWidth ?? 0
      );
    } catch (error) {
      return 0;
    }
  }, []);

  const [clientWidth, setClientWidth] = useState<number>(getClientWidth);
  const throttleTimeoutRef = useRef<number | null>(null);
  const mountedRef = useRef<boolean>(true);

  const throttledHandleResize = useCallback(() => {
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
    }

    throttleTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        const newWidth = getClientWidth();

        setClientWidth((prevWidth) => {
          return prevWidth !== newWidth ? newWidth : prevWidth;
        });
      }
    }, throttleMs) as unknown as number;
  }, [getClientWidth, throttleMs]);

  useEffect(() => {
    mountedRef.current = true;

    const initialWidth = getClientWidth();

    setClientWidth(initialWidth);

    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('resize', throttledHandleResize, { passive: true });
    window.addEventListener('orientationchange', throttledHandleResize, {
      passive: true,
    });

    return () => {
      mountedRef.current = false;

      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }

      window.removeEventListener('resize', throttledHandleResize);
      window.removeEventListener('orientationchange', throttledHandleResize);
    };
  }, [getClientWidth, throttledHandleResize]);

  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, []);

  return clientWidth;
}

export default useClientWidth;

export type UseClientWidthHook = typeof useClientWidth;
